import React from "react";
import {
	addTimeToDate,
	Button,
	Column,
	DateTime,
	DateTimePrecision,
	dateTrunc,
	formatLocaleDateTimeByPrecision,
	If,
	isAfter,
	isSameTime,
	Row,
	Rule,
	Timestamp,
	ViewableObserver,
} from "@hex-insights/core";
import {
	Message,
	MessageGroup,
	MessageGroupArchivePointOrderField,
	MessageGroupDetailQuery,
	MessageGroupMessagesListQuery,
	OrderDirection,
	useMessageGroupArchivePointListInMessageGroupQuery,
	UserViewRecordListForTargetWithUserQuery,
} from "../../../../../Utilities";
import { UserFormat } from "../../../../Format";
import { MessageGroupArchivePointFormValues } from "../../../../FormUtilities";
import { MessageGroupUtilities } from "../../../../Utilities";
import * as MessageComponents from "../../../Message";
import * as MessageGroupArchivePointComponents from "../../../MessageGroupArchivePoint";
import styles from "./styles.module.css";

export type BodyProps = {
	messageGroupID: MessageGroup["id"];
	messageGroup: MessageGroupDetailQuery["messageGroup"] | undefined;
};

export function Body({ messageGroupID, messageGroup }: BodyProps) {
	const otherUsers = messageGroup?.users.filter((e) => !e.isCurrent);
	const isOneOnOne = (otherUsers?.length ?? 0) === 1 && (messageGroup?.userGroups.length ?? 0) === 0;

	const [isAtBottom, setIsAtBottom] = React.useState(true);
	const onEnterBottom = React.useCallback(() => setIsAtBottom(true), []);
	const onExitBottom = React.useCallback(() => setIsAtBottom(false), []);

	const {
		loadingViewerViewRecord,
		lastViewedAt,
		otherUserViewRecords,
		data: messageData,
		loadMore,
		networkStatus,
	} = MessageGroupUtilities.useMessageGroupMessages({ messageGroupID, poll: isAtBottom });
	// TODO include other archive points
	const { data: archivePointData } = useMessageGroupArchivePointListInMessageGroupQuery({
		variables: {
			filters: { messageGroupIDEQ: messageGroupID },
			order: { field: MessageGroupArchivePointOrderField.CreatedAt, direction: OrderDirection.Desc },
			pagination: { first: 1 },
		},
		fetchPolicy: "cache-and-network",
	});
	const hasArchivePointData = !!archivePointData;
	const latestArchivePoint = archivePointData?.messageGroupArchivePointConnection?.edges[0]?.node ?? null;
	const [clickedShowPastLatestArchivePoint, setClickedShowPastLatestArchivePoint] = React.useState(false);
	MessageGroupUtilities.useMessageGroupScroll({ data: messageData, networkStatus });

	const items = React.useMemo(() => {
		if (!messageData || messageData.messageConnection.edges.length === 0 || !hasArchivePointData) {
			return [];
		}
		let section = {
			author: messageData.messageConnection.edges[0].node.author,
			messages: [] as MessageGroupMessagesListQuery["messageConnection"]["edges"][0]["node"][],
			messageViewRecords: {} as Record<
				Message["id"],
				UserViewRecordListForTargetWithUserQuery["userViewRecordConnection"]["edges"][0]["node"][]
			>,
			isNewMessagesDivider: false,
		};
		let hasDividedNewMessages = false;
		let hasPlacedArchivePoint = false;
		const items = [];
		for (let i = 0; i < messageData.messageConnection.edges.length; i++) {
			const message = messageData.messageConnection.edges[i].node;
			const prevMessage = i > 0 ? messageData.messageConnection.edges[i - 1].node : null;

			const isNewMessagesDivider =
				!message.author.isCurrent &&
				!loadingViewerViewRecord &&
				!hasDividedNewMessages &&
				lastViewedAt !== null &&
				isAfter(message.createdAt, lastViewedAt);
			if (isNewMessagesDivider) {
				hasDividedNewMessages = true;
			}

			const isArchivePointDivider =
				!hasPlacedArchivePoint &&
				latestArchivePoint !== null &&
				isAfter(message.createdAt, latestArchivePoint.createdAt);
			if (isArchivePointDivider) {
				hasPlacedArchivePoint = true;
			}

			const breakSection =
				message.author.id !== section.author.id ||
				(prevMessage !== null && !isSameTime(prevMessage.createdAt, message.createdAt, "day")) ||
				isNewMessagesDivider ||
				isArchivePointDivider;
			if (breakSection) {
				if (section.messages.length > 0) {
					items.push({
						itemTime: section.messages[0].createdAt,
						itemDate: dateTrunc(new Date(section.messages[0].createdAt), "day").toDate(),
						messageSection: section,
					});
				}
				if (isArchivePointDivider) {
					items.push({
						itemTime: latestArchivePoint.createdAt,
						itemDate: dateTrunc(new Date(latestArchivePoint.createdAt), "day").toDate(),
						archivePoint: latestArchivePoint,
					});
				}
				section = {
					author: message.author,
					messages: [],
					messageViewRecords: {},
					isNewMessagesDivider,
				};
			}
			section.messages.push(message);
			section.messageViewRecords[message.id] = [];
		}
		items.push({
			itemTime: section.messages[0].createdAt,
			itemDate: dateTrunc(new Date(section.messages[0].createdAt), "day").toDate(),
			messageSection: section,
		});
		if (latestArchivePoint !== null && !hasPlacedArchivePoint) {
			items.push({
				itemTime: latestArchivePoint.createdAt,
				itemDate: dateTrunc(new Date(latestArchivePoint.createdAt), "day").toDate(),
				archivePoint: latestArchivePoint,
			});
		}

		const flatMessages = [];
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (!item.messageSection) {
				continue;
			}
			for (let j = 0; j < item.messageSection.messages.length; j++) {
				const message = item.messageSection.messages[j];
				flatMessages.push({
					messageID: message.id,
					authorID: message.author.id,
					createdAt: message.createdAt,
					itemIndex: i,
					messageIndex: j,
					isRemoved: message.isRemoved,
				});
			}
		}
		flatMessages.reverse();
		for (let i = 0; i < otherUserViewRecords.length; i++) {
			const viewRecord = otherUserViewRecords[i];
			for (let j = 0; j < flatMessages.length; j++) {
				const messageInfo = flatMessages[j];
				if (
					viewRecord.user.id !== messageInfo.authorID &&
					!messageInfo.isRemoved &&
					isAfter(viewRecord.lastViewedAt, messageInfo.createdAt)
				) {
					items[messageInfo.itemIndex].messageSection?.messageViewRecords[messageInfo.messageID].push(viewRecord);
					break;
				}
			}
		}

		return items;
	}, [
		messageData,
		loadingViewerViewRecord,
		lastViewedAt,
		otherUserViewRecords,
		hasArchivePointData,
		latestArchivePoint,
	]);

	const isArchivePointLast = items.length > 0 && !!items[items.length - 1].archivePoint;
	const showPastLatestArchivePoint = clickedShowPastLatestArchivePoint || isArchivePointLast;
	const isArchivePointClipping = latestArchivePoint !== null && !showPastLatestArchivePoint;

	const onClickShowArchivedMessages = React.useCallback(() => {
		setClickedShowPastLatestArchivePoint(true);
		MessageGroupUtilities.preserveScrollPosition();
	}, []);

	return (
		<React.Fragment>
			<If condition={!isArchivePointClipping}>
				<ViewableObserver onEnterView={loadMore} rootMargin="150px 0px 0px" rateLimitMS={1000} />
			</If>
			<Column justify="space-between" className={styles["body"]}>
				<Column justify="spaced-start" verticalSpacing="1rem">
					{items.map(({ itemTime, itemDate, messageSection, archivePoint }, i) => {
						const showSection =
							latestArchivePoint === null ||
							showPastLatestArchivePoint ||
							(latestArchivePoint !== null && isAfter(itemTime, latestArchivePoint.createdAt));
						const isSameDayAsPrev = i === 0 || isSameTime(itemTime, items[i - 1].itemTime, "day");

						const dateDivider = (
							<If condition={!isSameDayAsPrev}>
								<Row justify="center" align="center">
									<Timestamp
										value={itemDate}
										format={formatDividerTimestamp}
										precision="day"
										className={styles["body__divider"]}
									/>
								</Row>
							</If>
						);

						if (messageSection) {
							if (!showSection) {
								return null;
							}

							const { author, messages, isNewMessagesDivider, messageViewRecords } = messageSection;
							if (messages.length === 0) {
								return null;
							}

							if (!showSection) {
								return null;
							}

							return (
								<React.Fragment key={messages[0].id}>
									<If condition={isNewMessagesDivider}>
										<Rule className={styles["body__divider"] + " " + styles["body__divider--new-messages"]}>
											<span className={styles["body__divider--new-messages__content"]}>New Messages</span>
										</Rule>
									</If>

									{dateDivider}

									<MessageComponents.MessageSection
										author={author}
										messages={messages}
										messageViewRecords={messageViewRecords}
										isInOneOnOne={isOneOnOne}
									/>
								</React.Fragment>
							);
						}

						if (archivePoint) {
							if (!showSection && archivePoint.id === latestArchivePoint.id) {
								return (
									<React.Fragment key={archivePoint.id}>
										<Row justify="center">
											<Button variant="link" size="small" onClick={onClickShowArchivedMessages}>
												Show Archived Messages
											</Button>
										</Row>
									</React.Fragment>
								);
							}

							if (!showSection) {
								return null;
							}

							return (
								<React.Fragment key={archivePoint.id}>
									{dateDivider}
									<Rule className={styles["body__divider"] + " " + styles["body__divider--archive-point"]}>
										<div className={styles["body__divider--archive-point__content"]}>
											{UserFormat.name(archivePoint.author)} archived this discussion
										</div>
									</Rule>
								</React.Fragment>
							);
						}

						return null;
					})}
				</Column>

				<If condition={!isArchivePointLast}>
					<Column
						justify="spaced-start"
						align="center"
						verticalSpacing="1rem"
						className={styles["body__built-in-content"]}
					>
						<Row justify="spaced-center" align="center" overflow="wrap" withVerticalSpacing>
							<If condition={!isArchivePointLast}>
								<ArchiveButton messageGroupID={messageGroupID} />
							</If>
						</Row>
					</Column>
				</If>
			</Column>
			<ViewableObserver onEnterView={onEnterBottom} onExitView={onExitBottom} rootMargin="0px 0px 150px" />
		</React.Fragment>
	);
}

function formatDividerTimestamp(value: DateTime, precision: DateTimePrecision) {
	if (isSameTime(value, new Date(), "day")) {
		return "Today";
	}
	if (isSameTime(value, addTimeToDate(new Date(), [-1, "day"]), "day")) {
		return "Yesterday";
	}
	return formatLocaleDateTimeByPrecision(value, precision);
}

type ArchiveButtonProps = {
	messageGroupID: MessageGroup["id"];
};

function ArchiveButton({ messageGroupID }: ArchiveButtonProps) {
	const archivePointCreateFormValues = React.useMemo<MessageGroupArchivePointFormValues.Create>(
		() => ({
			messageGroupID,
		}),
		[messageGroupID],
	);

	const { isViewerInMessageGroup } = MessageGroupUtilities.useIsViewerInMessageGroup(messageGroupID);
	if (!isViewerInMessageGroup) {
		return null;
	}

	return (
		<MessageGroupArchivePointComponents.MessageGroupArchivePointCreatePermissionRequired>
			<MessageGroupArchivePointComponents.CreateButton
				formValues={archivePointCreateFormValues}
				variant="link"
				size="small"
				iconSize={14}
				verticalAlign="bottom"
			/>
		</MessageGroupArchivePointComponents.MessageGroupArchivePointCreatePermissionRequired>
	);
}
